import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { isDemoMode } from '../demo/useDemoMode';
import { MOCK_CONVERSATIONS, MOCK_MESSAGES, getJobById, getUserById } from '../demo/mockData';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

const MessagesPage = () => {
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initialize socket connection
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Skip socket connection in demo mode
    if (isDemoMode()) return;

    const token = localStorage.getItem('token');
    socketRef.current = io(SOCKET_URL, {
      auth: { token }
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected');
    });

    socketRef.current.on('new_message', (message) => {
      setMessages(prev => [...prev, message]);
      scrollToBottom();
    });

    socketRef.current.on('message_notification', (data) => {
      // Update conversation list with new message
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === data.conversationId) {
            return {
              ...conv,
              last_message: data.message.content,
              last_message_time: data.message.created_at,
              unread_count: conv.id === activeConversation?.id ? 0 : (conv.unread_count || 0) + 1
            };
          }
          return conv;
        });
        // Sort by last message time
        return updated.sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time));
      });
    });

    socketRef.current.on('user_typing', (data) => {
      if (data.conversationId === activeConversation?.id) {
        setOtherUserTyping(true);
      }
    });

    socketRef.current.on('user_stopped_typing', (data) => {
      if (data.conversationId === activeConversation?.id) {
        setOtherUserTyping(false);
      }
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [isAuthenticated, activeConversation?.id]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    // In demo mode, use mock data
    if (isDemoMode()) {
      setConversations(MOCK_CONVERSATIONS);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch messages for active conversation
  const fetchMessages = useCallback(async (conversationId) => {
    // In demo mode, use mock data
    if (isDemoMode()) {
      const mockMsgs = MOCK_MESSAGES[conversationId] || [];
      setMessages(mockMsgs);
      scrollToBottom();
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messages/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      scrollToBottom();
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, []);

  // Handle URL params for starting a new conversation
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchConversations();

    const jobId = searchParams.get('job');
    const otherUserId = searchParams.get('user');

    if (otherUserId) {
      // In demo mode, create a mock conversation
      if (isDemoMode()) {
        const job = jobId ? getJobById(parseInt(jobId)) : null;
        const otherUser = getUserById(parseInt(otherUserId));
        
        // Check if conversation already exists
        let conv = MOCK_CONVERSATIONS.find(c => 
          c.other_user_id === parseInt(otherUserId) && 
          (!jobId || c.job_id === parseInt(jobId))
        );
        
        if (!conv && otherUser) {
          // Create a new mock conversation
          conv = {
            id: 100 + parseInt(otherUserId),
            other_user_id: parseInt(otherUserId),
            other_user_name: otherUser.name,
            other_user_image: null,
            job_id: jobId ? parseInt(jobId) : null,
            job_title: job?.title || null,
            job_image: null,
            job_price: job?.price || null,
            last_message: null,
            last_message_time: new Date().toISOString(),
            unread_count: 0
          };
        }
        
        if (conv) {
          setActiveConversation(conv);
          fetchMessages(conv.id);
        }
        return;
      }

      // Create or get conversation
      const createConversation = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${API_URL}/messages/conversations`, {
            otherUserId: parseInt(otherUserId),
            jobId: jobId ? parseInt(jobId) : null
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setActiveConversation(response.data);
          fetchMessages(response.data.id);
          socketRef.current?.emit('join_conversation', response.data.id);
          
          // Refresh conversations list
          fetchConversations();
        } catch (error) {
          console.error('Error creating conversation:', error);
        }
      };
      createConversation();
    }
  }, [isAuthenticated, searchParams, fetchConversations, fetchMessages]);

  // Join conversation room when active conversation changes
  useEffect(() => {
    if (activeConversation && socketRef.current) {
      socketRef.current.emit('join_conversation', activeConversation.id);
      fetchMessages(activeConversation.id);
    }

    return () => {
      if (activeConversation && socketRef.current) {
        socketRef.current.emit('leave_conversation', activeConversation.id);
      }
    };
  }, [activeConversation, fetchMessages]);

  // Handle typing indicator
  const handleTyping = () => {
    if (activeConversation && socketRef.current) {
      socketRef.current.emit('typing', { conversationId: activeConversation.id });
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current.emit('stop_typing', { conversationId: activeConversation.id });
      }, 2000);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedImage) || !activeConversation) return;

    setSending(true);

    // In demo mode, add message locally
    if (isDemoMode()) {
      const demoMessage = {
        id: Date.now(),
        conversation_id: activeConversation.id,
        sender_id: user.id,
        sender_name: user.name,
        sender_image: null,
        content: newMessage.trim(),
        image_url: null,
        created_at: new Date().toISOString(),
        read_at: null
      };
      
      setMessages(prev => [...prev, demoMessage]);
      setNewMessage('');
      setSelectedImage(null);
      scrollToBottom();
      setSending(false);
      
      // Update conversation in list
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === activeConversation.id) {
            return {
              ...conv,
              last_message: demoMessage.content,
              last_message_time: demoMessage.created_at
            };
          }
          return conv;
        });
        return updated.sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time));
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (newMessage.trim()) {
        formData.append('content', newMessage.trim());
      }
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      const response = await axios.post(
        `${API_URL}/messages/conversations/${activeConversation.id}/messages`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      setSelectedImage(null);
      scrollToBottom();

      // Update conversation in list
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === activeConversation.id) {
            return {
              ...conv,
              last_message: response.data.content,
              last_message_time: response.data.created_at
            };
          }
          return conv;
        });
        return updated.sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time));
      });

    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'I går';
    } else if (days < 7) {
      return date.toLocaleDateString('nb-NO', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
    }
  };

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">
            lock
          </span>
          <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
            Du må være logget inn
          </h2>
          <p className="text-slate-500 mb-4">
            Logg inn for å se dine meldinger
          </p>
          <Link 
            to="/logg-inn"
            className="inline-block bg-[#0A1929] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors"
          >
            Logg inn
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-56px-64px)] md:h-[calc(100vh-64px)] flex flex-col md:flex-row bg-slate-50">
      {/* Conversations List - Hidden on mobile when conversation is active */}
      <div className={`${activeConversation ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 bg-white border-r border-slate-200`}>
        <div className="p-4 border-b border-slate-200">
          <h1 className="font-heading font-semibold text-lg text-[#0A1929]">Meldinger</h1>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                    <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center">
              <span className="material-symbols-outlined text-4xl text-slate-300 mb-2 block">chat_bubble</span>
              <p className="text-slate-500 text-sm">Ingen samtaler ennå</p>
              <p className="text-slate-400 text-xs mt-1">
                Start en samtale ved å kontakte noen på en annonse
              </p>
            </div>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => setActiveConversation(conv)}
                className={`w-full p-4 flex gap-3 hover:bg-slate-50 transition-colors text-left ${
                  activeConversation?.id === conv.id ? 'bg-slate-100' : ''
                }`}
              >
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center overflow-hidden">
                    {conv.other_user_image ? (
                      <img src={conv.other_user_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white font-bold">
                        {conv.other_user_name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#0A1929] truncate">{conv.other_user_name}</span>
                    <span className="text-xs text-slate-400">{formatTime(conv.last_message_time)}</span>
                  </div>
                  {conv.job_title && (
                    <p className="text-xs text-[#1E3A5F] truncate">{conv.job_title}</p>
                  )}
                  <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-medium text-[#0A1929]' : 'text-slate-500'}`}>
                    {conv.last_message || 'Ingen meldinger'}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      {activeConversation ? (
        <div className="flex-1 flex flex-col bg-white">
          {/* Chat Header */}
          <div className="p-4 border-b border-slate-200 flex items-center gap-3">
            <button
              onClick={() => setActiveConversation(null)}
              className="md:hidden p-1"
            >
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <Link 
              to={`/profil/${activeConversation.other_user_id || activeConversation.other_user?.id}`}
              className="flex items-center gap-3 flex-1"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center overflow-hidden">
                {(activeConversation.other_user_image || activeConversation.other_user?.profile_image) ? (
                  <img src={activeConversation.other_user_image || activeConversation.other_user?.profile_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-bold">
                    {(activeConversation.other_user_name || activeConversation.other_user?.name)?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h2 className="font-medium text-[#0A1929]">
                  {activeConversation.other_user_name || activeConversation.other_user?.name}
                </h2>
                {otherUserTyping && (
                  <p className="text-xs text-[#1E3A5F]">Skriver...</p>
                )}
              </div>
            </Link>
            {activeConversation.job_id && (
              <Link 
                to={`/annonse/${activeConversation.job_id}`}
                state={{ fromChat: true }}
                className="text-sm text-[#1E3A5F] hover:underline hidden md:block"
              >
                Se annonse
              </Link>
            )}
          </div>

          {/* Job Reference Widget - vises rett under header */}
          {activeConversation.job_id && (activeConversation.job_title || activeConversation.job) && (
            <Link 
              to={`/annonse/${activeConversation.job_id}`}
              state={{ fromChat: true }}
              className="flex items-center gap-3 p-3 bg-slate-50 border-b border-slate-100 hover:bg-slate-100 transition-colors"
            >
              {/* Job image */}
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#1E3A5F] to-[#0A1929]">
                {activeConversation.job_image ? (
                  <img 
                    src={activeConversation.job_image} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="material-symbols-outlined text-white/50 text-lg">work</span>
                  </div>
                )}
              </div>
              
              {/* Job info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 mb-0.5">Samtalen gjelder:</p>
                <p className="font-medium text-[#0A1929] text-sm truncate">
                  {activeConversation.job_title || 'Annonse'}
                </p>
                {activeConversation.job_price && (
                  <p className="text-xs text-[#1E3A5F] font-medium">
                    {activeConversation.job_price.toLocaleString('nb-NO')} kr
                  </p>
                )}
              </div>

              {/* Arrow */}
              <span className="material-symbols-outlined text-slate-400">chevron_right</span>
            </Link>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => {
              const isOwn = msg.sender_id === user.id;
              const showAvatar = !isOwn && (index === 0 || messages[index - 1].sender_id !== msg.sender_id);
              
              return (
                <div
                  key={msg.id}
                  className={`flex gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  {!isOwn && (
                    <div className="w-8 h-8 flex-shrink-0">
                      {showAvatar && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center overflow-hidden">
                          {msg.sender_image ? (
                            <img src={msg.sender_image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white text-xs font-bold">
                              {msg.sender_name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className={`max-w-[70%] ${isOwn ? 'order-1' : ''}`}>
                    {msg.image_url && (
                      <img 
                        src={`${SOCKET_URL}${msg.image_url}`} 
                        alt="" 
                        className="rounded-lg max-w-full mb-1 cursor-pointer"
                        onClick={() => window.open(`${SOCKET_URL}${msg.image_url}`, '_blank')}
                      />
                    )}
                    {msg.content && (
                      <div className={`px-4 py-2 rounded-2xl ${
                        isOwn 
                          ? 'bg-[#0A1929] text-white rounded-br-sm' 
                          : 'bg-slate-100 text-[#0A1929] rounded-bl-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    )}
                    <p className={`text-xs text-slate-400 mt-1 ${isOwn ? 'text-right' : ''}`}>
                      {formatTime(msg.created_at)}
                      {isOwn && msg.read_at && (
                        <span className="ml-1 text-[#1E3A5F]">✓</span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Image Preview */}
          {selectedImage && (
            <div className="px-4 py-2 border-t border-slate-100">
              <div className="relative inline-block">
                <img 
                  src={URL.createObjectURL(selectedImage)} 
                  alt="Preview" 
                  className="h-20 rounded-lg"
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
            </div>
          )}

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-slate-400 hover:text-[#1E3A5F] transition-colors"
              >
                <span className="material-symbols-outlined">image</span>
              </button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
                }}
                placeholder="Skriv en melding..."
                className="flex-1 px-4 py-2 bg-slate-100 rounded-full border-0 focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
              />
              <button
                type="submit"
                disabled={sending || (!newMessage.trim() && !selectedImage)}
                className="p-2 bg-[#0A1929] text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1E3A5F] transition-colors"
              >
                <span className="material-symbols-outlined">
                  {sending ? 'progress_activity' : 'send'}
                </span>
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center bg-slate-50">
          <div className="text-center">
            <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">forum</span>
            <p className="text-slate-500">Velg en samtale for å se meldinger</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
